import { useEffect, useMemo, useState } from "react";

import { api } from "@/lib/api";
import { AREA_METROPOLITANA_MUNICIPALITIES } from "@/lib/polenica";

function titleCase(value) {
  return String(value || "")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
    .join(" ");
}

function formatComunaLabel(code, name) {
  const cleanCode = String(code || "")
    .replace(/\D/g, "")
    .padStart(2, "0");

  if (!cleanCode) {
    return titleCase(name);
  }

  return `Comuna ${parseInt(cleanCode, 10)}${name ? ` - ${titleCase(name)}` : ""}`;
}

let territoryPromise = null;

async function fetchLocalGeoJson(path) {
  const response = await fetch(path, { cache: "force-cache" });
  if (!response.ok) {
    throw new Error(`No fue posible cargar ${path}`);
  }
  return response.json();
}

function mergeTerritoryData(comunas, barriosByComuna, territoryCatalog = null) {
  const catalogMunicipalities = (territoryCatalog?.municipalities || [])
    .map((item) => ({
      value: item.value,
      label: item.displayName || item.value,
      barrios: (item.barrios || [])
        .map((barrio) => ({
          value: barrio.value,
          label: barrio.displayName || barrio.value,
        }))
        .filter((item) => item.value)
        .sort((left, right) => left.label.localeCompare(right.label, "es", { numeric: true })),
    }))
    .filter((item) => item.value);

  const municipalityMap = new Map();
  [...AREA_METROPOLITANA_MUNICIPALITIES, ...catalogMunicipalities].forEach((item) => {
    const existing = municipalityMap.get(item.value);
    municipalityMap.set(item.value, {
      ...existing,
      ...item,
      barrios: item.barrios || existing?.barrios || [],
    });
  });

  const catalogBarriosByMunicipio = {};
  Array.from(municipalityMap.values()).forEach((municipality) => {
    if (municipality.barrios?.length) {
      catalogBarriosByMunicipio[municipality.value] = municipality.barrios;
    }
  });

  return {
    municipalities: Array.from(municipalityMap.values()).sort((left, right) =>
      left.label.localeCompare(right.label, "es", { numeric: true }),
    ),
    comunas,
    barriosByComuna,
    barriosByMunicipio: catalogBarriosByMunicipio,
  };
}

function buildTerritoryData(comunasData, barriosData, territoryCatalog = null) {
  const comunas = (comunasData?.features || [])
    .map((feature) => {
      const properties = feature?.properties || {};
      const label = formatComunaLabel(
        properties.comuna_code || properties.codigo_comuna || properties.comuna,
        properties.nombre_comuna,
      );
      return {
        value: label,
        label,
      };
    })
    .filter((item) => item.value)
    .sort((left, right) => left.label.localeCompare(right.label, "es", { numeric: true }));

  const seenComunas = new Set();
  const uniqueComunas = comunas.filter((item) => {
    if (seenComunas.has(item.value)) {
      return false;
    }
    seenComunas.add(item.value);
    return true;
  });

  const barriosByComuna = {};
  (barriosData?.features || []).forEach((feature) => {
    const properties = feature?.properties || {};
    const comunaKey = formatComunaLabel(
      properties.comuna_code || properties.codigo_comuna || properties.comuna,
      properties.nombre_comuna,
    );
    const barrioName = titleCase(properties.nombre_barrio || properties.barrio || properties.nombre);
    if (!comunaKey || !barrioName) {
      return;
    }
    if (!barriosByComuna[comunaKey]) {
      barriosByComuna[comunaKey] = [];
    }
    barriosByComuna[comunaKey].push({
      value: barrioName,
      label: barrioName,
    });
  });

  Object.keys(barriosByComuna).forEach((key) => {
    const seen = new Set();
    barriosByComuna[key] = barriosByComuna[key]
      .filter((item) => {
        if (seen.has(item.value)) {
          return false;
        }
        seen.add(item.value);
        return true;
      })
      .sort((left, right) => left.label.localeCompare(right.label, "es", { numeric: true }));
  });

  return mergeTerritoryData(uniqueComunas, barriosByComuna, territoryCatalog);
}

function buildTerritoryDataFromOptions(optionsData, territoryCatalog = null) {
  const comunas = (optionsData?.comunas || []).map((item) => ({
    value: item.value,
    label: item.label || item.value,
  }));
  const barriosByComuna = {};
  (optionsData?.comunas || []).forEach((item) => {
    barriosByComuna[item.value] = (item.barrios || []).map((barrio) => ({
      value: barrio.value,
      label: barrio.label || barrio.value,
    }));
  });
  return mergeTerritoryData(comunas, barriosByComuna, territoryCatalog);
}

function assertTerritoryData(data) {
  if (!data.comunas.length || !Object.keys(data.barriosByComuna).length) {
    throw new Error("Catalogo territorial incompleto.");
  }
  return data;
}

async function loadLocalTerritoryData() {
  const [optionsData, territoryCatalog] = await Promise.all([
    fetchLocalGeoJson("/data/medellin-territory-options.json"),
    fetchLocalGeoJson("/data/antioquia-territory-catalog.json").catch(() => null),
  ]);
  return assertTerritoryData(buildTerritoryDataFromOptions(optionsData, territoryCatalog));
}

async function loadApiTerritoryData() {
  const [comunasResponse, barriosResponse] = await Promise.all([
    api.get("/map/layers/comunas"),
    api.get("/map/layers/barrios"),
  ]);
  return assertTerritoryData(buildTerritoryData(comunasResponse.data, barriosResponse.data));
}

async function loadTerritoryData() {
  if (!territoryPromise) {
    territoryPromise = loadLocalTerritoryData().catch(() => loadApiTerritoryData());
  }

  return territoryPromise;
}

export function useTerritoryCatalog() {
  const [territory, setTerritory] = useState({
    municipalities: AREA_METROPOLITANA_MUNICIPALITIES,
    comunas: [],
    barriosByComuna: {},
    barriosByMunicipio: {},
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    loadTerritoryData()
      .then((data) => {
        if (active) {
          setTerritory(data);
        }
      })
      .catch(() => {
        if (active) {
          setTerritory({
            municipalities: AREA_METROPOLITANA_MUNICIPALITIES,
            comunas: [],
            barriosByComuna: {},
            barriosByMunicipio: {},
          });
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const getComunasForMunicipio = useMemo(
    () => (municipio) => (municipio === "Medellin" ? territory.comunas : []),
    [territory.comunas],
  );

  const getBarriosForComuna = useMemo(
    () => (municipio, comuna) => {
      if (municipio !== "Medellin") {
        return territory.barriosByMunicipio[municipio] || [];
      }
      return territory.barriosByComuna[comuna] || [];
    },
    [territory.barriosByComuna, territory.barriosByMunicipio],
  );

  return {
    loading,
    municipalities: territory.municipalities,
    comunas: territory.comunas,
    barriosByComuna: territory.barriosByComuna,
    getComunasForMunicipio,
    getBarriosForComuna,
  };
}
